import type { Command, Perspective } from '@integrate/contracts';
import {
  ORG_HEAD_GROUP,
  currentBucket,
  getHeadFor,
  getHeadSource,
  type SeparationState,
} from '@integrate/core';
import { ICON, IconButton } from '../ui/Icon.js';
import { removeHeadCommand } from './heads.js';

/**
 * The whole organisation, which is what the root card stands for.
 *
 * IT USED TO BE ONE SENTENCE. "The whole organisation. Choose a group or a person to work on them."
 * — which was true and was also the entire panel, while the organisation had a head that
 * `hierarchy.ts:339` was drawing on the very card the reader had just clicked. So the org head was
 * designated from any person's panel, reported in the Data panel's coverage line, and painted on the
 * root card, and the one place that stood for the organisation itself said nothing about them.
 *
 * That is the same pair of gaps the group panel had: no way to reach the head, and no way to take the
 * designation back except from the head's own panel, which you had to find first. `G:__org__` is a
 * group id like any other, so the fix is the same fix and shares the same `removeHeadCommand`.
 *
 * THE DESIGNATION IS READ FROM THE DESIGN, NOT FROM THE DRAWN CARD, and those can differ: the chart
 * drops the org head from the root when a filter hides them (`hierarchy.ts:341-347`). The panel still
 * names them, deliberately — a designation is a fact about the design, and a panel that went quiet
 * because of a lens would have somebody re-designating a head they already have.
 */
export function OrgPanel({
  projectName,
  state,
  perspective,
  targetView,
  pivot,
  readOnly,
  scenarioId,
  onDispatch,
  onSelectPerson,
}: {
  projectName: string;
  /**
   * Null while the design is still loading.
   *
   * The root can be selected before it lands — the address bar carries `?node=` across a reload, so
   * the selection is restored before the fetch returns. The sentence below is worth showing in that
   * window; the head row is not, because there is nothing yet to resolve it against.
   */
  state: SeparationState | null;
  perspective: Perspective;
  /** The blueprint has no heads — `hierarchy.ts:339` refuses to resolve one, so nor does this. */
  targetView: boolean;
  /** Nor does a pivot: a pivot group is a bucket of field values, not a group the organisation has. */
  pivot: boolean;
  readOnly: boolean;
  scenarioId: string;
  onDispatch(command: Command): void;
  onSelectPerson(employeeId: string): void;
}): JSX.Element {
  /*
    Resolved once, behind the one guard, so nothing below has to assert the state is there. `null`
    means "this view cannot have an org head" — a loading design, the blueprint, or a pivot — which is
    a different statement from "it has none", and the row is absent rather than empty for it.
  */
  const designation =
    state === null || targetView || pivot
      ? null
      : (() => {
          const bucketOf = (id: string): string => currentBucket(state.design, id);
          const headId = getHeadFor(state, ORG_HEAD_GROUP, perspective, bucketOf);
          /* Build-ups included, for the reason the group panel gives: a planned hire can head one. */
          const person = headId
            ? state.employees.find((e) => e.id === headId) ??
              state.design.buildUps.find((b) => b.id === headId) ??
              null
            : null;
          return {
            state,
            person,
            source: getHeadSource(state, ORG_HEAD_GROUP, perspective, bucketOf),
          };
        })();

  return (
    <section data-testid="root-selected">
      <h2>{projectName}</h2>

      {designation && (
        <div className="sp-row">
          <span className="k">Head of the organisation</span>
          <span className="v" data-testid="org-head">
            {designation.person ? (
              <>
                <button
                  className="linkish"
                  data-testid="org-head-open"
                  title={`Open ${designation.person.name}`}
                  onClick={() => onSelectPerson(String(designation.person!.id))}
                >
                  {designation.person.name}
                </button>
                {designation.source === 'inherited' && <span className="pill">inherited</span>}
                {!readOnly && (
                  <IconButton
                    icon={ICON.remove}
                    testId="org-head-remove"
                    label={`Remove ${designation.person.name} as head of the organisation`}
                    onClick={() =>
                      onDispatch(
                        removeHeadCommand(designation.state, {
                          scenarioId,
                          perspective,
                          groupId: ORG_HEAD_GROUP,
                          headId: String(designation.person!.id),
                        }),
                      )
                    }
                  />
                )}
              </>
            ) : (
              /* Designating one is a person's own panel, which is where somebody is chosen. */
              '— not designated'
            )}
          </span>
        </div>
      )}

      <p className="muted">The whole organisation. Choose a group or a person to work on them.</p>
    </section>
  );
}
